import { ofType } from 'redux-observable';
import { Observable, EMPTY, from, of, forkJoin, concat } from 'rxjs';
import { take, filter, map, mergeMap, catchError } from 'rxjs/operators';
import queryString from 'qs';
import get from 'lodash.get';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { emptyArray } from 'ui-components/default-props/empty';
import Global from '../../store/global-selectors';
import { ModalService } from 'ui-components/services/modal-service';
import * as NavActions from '../../store/returns/nav/returns.nav.actions';
import * as GridActions from '../../store/returns/grid/returns.grid.actions';
import * as ReturnDetailsActions from '../../store/returns/details/returns.details.actions';
import { SetReceivedDateModal } from '../../components/modals/set-received-date-modal';
import { EmailReturnLabelModal } from '../../components/modals/email-return-label-modal';
import { CancelReturnLabelPromptModalContainer } from '../../containers/cancel-return-label-prompt-modal-container';
import * as FilteringActions from '../../store/returns/filtering/returns.filtering.actions';
import { mapCriterionToApiParam } from '../../services/filtering/api-param-map-service';
import { ColumnOrderByParamService } from '../../services/column-orderby-param-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_INFO } from 'ui-components/services/notification-service';
import { NotificationHubService } from '../notification-hub/notification-hub-service';
import { NotificationType } from '../notification-hub/notification-hub.types';
import { notificationDescriptionMessages } from '../notification-hub/notification-messages';
import { DateService } from '../../services/date-service';
import { DocumentService } from '../../services/document-service';
import { createLabelSubject } from '../../fulfillment-planner/context';
import { PusherService } from '../../services/pusher-service';
import { CORE_JOBSERVICE_JOB_COMPLETE } from '../../services/pusher-service/pusher-contract-messages';
import { showReturnEmailFailedNotification, getReturnsPageBasePath, selectIsEnhancedReturnsFeatureEnabled } from './returns.helpers';

import * as PrintActions from '../../store/printing/print.actions';
import * as UserActions from '../../store/user/user.actions';
import * as LabelApi from '../../store/labels/labels.api';
import * as ODActions from '../../store/orders/order-details/orders.details.actions';

import * as Actions from './returns.actions';
import * as Api from './returns.api';
import { messages } from './messages';
import { QUICK_SEARCH_LOCATION_KEY } from '../../services/quicksearch-location-service';
import { bulkLoadLabelFulfillments$ } from '../bulkload/bulkload.observables';

export const changePageEpic = (action$, _, { push }) => {
  return action$.pipe(
    ofType(GridActions.RETURNS_GRID_CHANGE_PAGE),
    mergeMap(({ newPage, pageSize }) => {
      push({ search: queryString.stringify({ page: newPage, pageSize }) });
      return of(Actions.getReturns());
    }),
  );
};

export const getReturnsGridDataEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.RETURNS_GRID_GET_RETURNS_REQUEST, Actions.RETURNS_GRID_GET_SILENT_RETURNS_REQUEST),
    mergeMap(() => {
      const state = getState();
      const statusViewId = Global.selectReturnsNavSelectedViewId(state);
      if (statusViewId === 'advanced-search') {
        return EMPTY;
      }
      const statePage = Global.selectReturnsGridPage(state);
      const statePageSize = Global.selectReturnsGridPageSize(state);
      const appliedFilters = Global.selectReturnsFilteringAppliedFiltersByViewId(state, {
        statusViewId,
      });
      const filtersAsApiParams = appliedFilters.map(mapCriterionToApiParam);
      const extraParams = [...filtersAsApiParams];
      const orderByColumn = Global.selectReturnsGridCurrentOrderByColumn(state, { statusViewId });
      const orderByParam = ColumnOrderByParamService.getReturnsGridOrderByApiParamOrDefault(orderByColumn);
      extraParams.push({ orderBy: orderByParam });

      return Api.getReturns(statusViewId, statePage, statePageSize, extraParams).pipe(
        mergeMap((data) => {
          const { pageData: returns, page, pageSize, totalCount } = data;
          const fulfillmentIds = returns?.map((r) => r.fulfillmentId) ?? [];
          return concat(bulkLoadLabelFulfillments$(fulfillmentIds), of(Actions.getReturnsSuccess(returns, page, pageSize, totalCount)));
        }),
        catchError((error) => of(Actions.getReturnsFailure(error))),
      );
    }),
  );
};

export const exportReturns = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.EXPORT_RETURNS),
    mergeMap(({ fulfillmentIds }) => {
      const state = getState();
      const statusViewId = Global.selectReturnsNavSelectedViewId(state);
      const appliedFilters = Global.selectReturnsFilteringAppliedFiltersByViewId(state, {
        statusViewId,
      });
      const filtersAsApiParams = appliedFilters.map(mapCriterionToApiParam);
      const extraParams = [...filtersAsApiParams];

      if (fulfillmentIds) {
        extraParams.push({ fulfillmentIds });
      }

      const userId = Global.selectUserCurrentUserId(state);

      return from(Api.exportReturns(statusViewId, extraParams)).pipe(
        mergeMap(() => {
          return new Observable((observer) => {
            const onMessage = (message) => observer.next(message);
            const unbindFromEvent = PusherService.bindToEvent(CORE_JOBSERVICE_JOB_COMPLETE, onMessage);
            return () => unbindFromEvent();
          }).pipe(
            filter((pusherMessage) => pusherMessage.Notification.UserID === userId),
            take(1),
            map((msg) => {
              const hrefPattern = /href=".*"/g;
              return msg.Notification.Message.match(hrefPattern)[0];
            }),
            map((href) => {
              // strip href= and nested quotes
              return href.substring(href.indexOf('=') + 2, href.length - 1);
            }),
            mergeMap((url) => {
              const downloadUrl = url.trim();
              DocumentService.downloadFile(downloadUrl);
              NotificationHubService.showNotification(
                {
                  notificationType: NotificationType.RETURNS_EXPORT_COMPLETED,
                  defaultTitle: messages.returnsExportCompleteTitle.defaultMessage,
                  defaultDescription: notificationDescriptionMessages.RETURNS_EXPORT_COMPLETED.defaultMessage,
                  metadataJson: JSON.stringify({ downloadUrl }),
                },
                {
                  notificationType: NOTIFICATION_TYPE_INFO,
                  title: <FormattedMessage {...messages.returnsExportCompleteTitle} />,
                  content: <FormattedMessage {...messages.returnsExportCompleteBody} />,
                },
              );
              return EMPTY;
            }),
          );
        }),
      );
    }),
  );
};

export const changeShipmentsUrl = (action$, { getState }, { push, history }) => {
  return action$.pipe(
    ofType(NavActions.RETURNS_CHANGE_VIEW),
    mergeMap(({ viewId }) => {
      const state = getState();
      const isEnhancedReturnsFeatureEnabled = selectIsEnhancedReturnsFeatureEnabled(state);
      const historyPathname = history.location.pathname || '';
      const returnsPath = getReturnsPageBasePath(historyPathname, isEnhancedReturnsFeatureEnabled);

      const statePage = Global.selectReturnsGridPage(state);
      const statePageSize = Global.selectReturnsGridPageSize(state);
      const quickSearchTerm = new URLSearchParams(history.location.search).get(QUICK_SEARCH_LOCATION_KEY);

      const queryParams = { page: statePage, pageSize: statePageSize };
      if (isEnhancedReturnsFeatureEnabled && quickSearchTerm) {
        queryParams.quickSearch = quickSearchTerm;
      }

      const search = queryString.stringify(queryParams);
      const pathname = historyPathname.includes(viewId) ? historyPathname : `${returnsPath}/${viewId}`;

      push({ pathname, search });
      return EMPTY;
    }),
  );
};

export const openSetReceivedDateModal = (action$, { getState }) => {
  return action$.pipe(
    ofType(GridActions.RETURNS_GRID_OPEN_SET_RECEIVED_DATE_MODAL, GridActions.RETURNS_GRID_OPEN_UPDATE_RECEIVED_DATE_MODAL),
    mergeMap((action) => {
      const type = get(action, 'type');
      ModalService.launchModal({
        component: SetReceivedDateModal,
        props: {
          modalType: type === 'RETURNS_GRID_OPEN_UPDATE_RECEIVED_DATE_MODAL' ? 'updateReceivedDate' : 'markAsReceived',
          selectedReturns: Global.selectSelectedReturnsSelectedReturnIds(getState()),
          isFromGrid: true,
        },
      });
      return EMPTY;
    }),
  );
};

export const markAsReceived = (action$) => {
  return action$.pipe(
    ofType(Actions.RETURNS_MARK_AS_RECEIVED_REQUEST),
    mergeMap((action) => {
      const { receivedDateTime, selectedReturns, isFromGrid } = action;
      const dateTime = receivedDateTime || DateService.getFormattedCurrentDateTime();

      return Api.setReceivedDateOnReturn(selectedReturns, dateTime).pipe(
        mergeMap(() => {
          const success$ = isFromGrid
            ? of(GridActions.markAsReceivedSuccess())
            : of(ReturnDetailsActions.markAsReceivedSuccess(selectedReturns[0]));
          return success$;
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const refreshGrid = (action$) => {
  return action$.pipe(
    ofType(
      FilteringActions.RETURNS_CHANGE_FILTERS,
      GridActions.RETURNS_GRID_CHANGE_ORDERBY,
      ReturnDetailsActions.RETURN_DETAILS_MARK_AS_RECEIVED_SUCCESS,
      GridActions.RETURNS_GRID_MARK_AS_RECEIVED_SUCCESS,
      Actions.RETURNS_CANCEL_RETURN_LABEL_SUCCESS,
    ),
    mergeMap(() => {
      return of(Actions.getReturns());
    }),
  );
};

export const openReturnLabelEmailModal = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.RETURNS_OPEN_EMAIL_RETURN_LABEL_MODAL),
    mergeMap((action) => {
      let returns = get(action, 'returns');
      if (!returns) {
        const state = getState();
        const selectedReturnIds = Global.selectSelectedReturnsSelectedReturnIds(state);
        const allTheReturns = Global.selectReturnsGridReturns(state);
        returns = allTheReturns.filter((r) => selectedReturnIds.indexOf(r.fulfillmentId) > -1);
      }

      ModalService.launchModal({
        component: EmailReturnLabelModal,
        props: {
          returns,
        },
      });
      return EMPTY;
    }),
  );
};

export const sendReturnLabelsToEmails = (action$) => {
  return action$.pipe(
    ofType(Actions.RETURNS_SEND_LABEL_TO_EMAIL_REQUEST),
    mergeMap((action) => {
      const emails = get(action, 'emails', emptyArray);
      const cleanedEmails = emails.map((email) => ({
        fulfillmentId: email.fulfillmentId,
        emails: email.emailTo,
      }));
      return Api.postEmailsForReturnLabel(cleanedEmails).pipe(
        mergeMap(() => EMPTY),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const returnLabelEmailProcessedSuccessfully = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.RETURNS_LABEL_EMAIL_PROCESSING_SUCCESS),
    mergeMap((action) => {
      const emailLabelNotificationProps = {
        title: <FormattedMessage {...messages.successfulLabelTitle} />,
        content: <FormattedMessage {...messages.labelsProcessedSuccessfully} />,
        notificationType: 'info',
      };
      NotificationService.showNotification({ ...emailLabelNotificationProps });

      const fulfillmentId = get(action, 'statuses.0.fulfillmentId', '0');
      const returnDetailModalId = Global.selectReturnDetailsReturnDetailModalIdByFulfillmentId(getState(), { fulfillmentId });
      if (returnDetailModalId) {
        return of(ReturnDetailsActions.requestReturnDetails(returnDetailModalId));
      }

      return of(ODActions.requestOrderDetails());
    }),
  );
};

export const returnLabelEmailProcessedFailure = (action$) => {
  return action$.pipe(
    ofType(Actions.RETURNS_LABEL_EMAIL_PROCESSING_FAILURE),
    mergeMap((action) => {
      const firstErrorMessage = get(action, 'errors.0.error', emptyArray);
      showReturnEmailFailedNotification(firstErrorMessage);
      return EMPTY;
    }),
  );
};

export const saveGridConfigurationChanges = (action$) => {
  return action$.pipe(
    ofType(GridActions.RETURNS_GRID_CHANGE_COLUMNS, GridActions.RETURNS_GRID_CHANGE_ORDERBY),
    mergeMap(({ columns, statusViewId }) => {
      const setting = {
        settingName: `returns.gridConfig.${statusViewId}`,
        settingValue: JSON.stringify(columns),
      };
      return of(UserActions.saveUserSetting(setting));
    }),
  );
};

export const openCancelReturnLabelModalForGrid = (action$, { getState }) => {
  return action$.pipe(
    ofType(GridActions.RETURNS_GRID_VOID_AND_CANCEL_RETURN),
    mergeMap(() => {
      const state = getState();
      const selectedReturnIds = Global.selectSelectedReturnsSelectedReturnIds(state);
      const allTheReturns = Global.selectReturnsGridReturns(state);
      const returns = allTheReturns.filter((r) => selectedReturnIds.indexOf(r.fulfillmentId) > -1).map((r) => r.fulfillmentId);
      return of(Actions.openCancelReturnLabelModal(returns));
    }),
  );
};

export const openCancelReturnLabelModal = (action$) => {
  return action$.pipe(
    ofType(Actions.RETURNS_OPEN_CANCEL_RETURN_LABEL_MODAL),
    mergeMap((action) => {
      const { fulfillmentIds, fromOrderDetails } = action;
      ModalService.launchModal({
        component: CancelReturnLabelPromptModalContainer,
        props: {
          fulfillmentIds,
          fromOrderDetails,
          title: <FormattedMessage {...messages.cancelReturnTitle} />,
          message: <FormattedMessage {...messages.cancelReturnContent} />,
          confirmation: <FormattedMessage {...messages.cancelReturnConfirmation} />,
        },
      });
      return EMPTY;
    }),
  );
};

export const cancelAndVoidReturnShipment = (action$) => {
  return action$.pipe(
    ofType(Actions.RETURNS_CANCEL_RETURN_LABEL_REQUEST),
    mergeMap(({ fulfillmentIds, fromOrderDetails }) => {
      if (!fulfillmentIds || fulfillmentIds.length === 0) {
        return EMPTY;
      }
      const theVoid$ = fulfillmentIds.map((id) => LabelApi.voidSingleLabel(id));
      return forkJoin(theVoid$).pipe(
        mergeMap((responses) => of(Actions.cancelReturnLabelSuccess(responses, fulfillmentIds, fromOrderDetails))),
        catchError((err) => of(Actions.cancelReturnLabelFailure(err))),
      );
    }),
  );
};

export const cancelAndVoidReturnShipmentSuccess = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.RETURNS_CANCEL_RETURN_LABEL_SUCCESS),
    mergeMap((action) => {
      const responses = get(action, 'responses', emptyArray);
      const voidMessages = responses.flat().map((resp) => ({ message: resp.message, success: resp.success }));
      const fromOrderDetails = get(action, 'fromOrderDetails', false);
      voidMessages.forEach((message) => {
        const title = message.success ? <FormattedMessage {...messages.voidSuccessful} /> : <FormattedMessage {...messages.voidFailure} />;
        const voidNotificationProps = {
          title,
          content: message.message,
          notificationType: message.success ? 'info' : 'error',
        };
        NotificationService.showNotification({ ...voidNotificationProps });
      });

      if (fromOrderDetails) {
        return of(ODActions.requestOrderDetails());
      }

      const state = getState();
      const fulfillmentIds = get(action, 'fulfillmentIds');
      if (fulfillmentIds.length === 1) {
        const [fulfillmentId] = fulfillmentIds;
        const returnDetailModalId = Global.selectReturnDetailsReturnDetailModalIdByFulfillmentId(state, {
          fulfillmentId,
        });
        const visibleReturn = Global.selectReturnDetailsReturnDetailsByReturnDetailModalId(state, {
          returnDetailModalId,
        });
        const modalId = get(visibleReturn, 'modalId', '');
        ModalService.closeModalById(modalId);
      }
      return EMPTY;
    }),
  );
};

export const cancelAndVoidReturnShipmentFailure = (action$) => {
  return action$.pipe(
    ofType(Actions.RETURNS_CANCEL_RETURN_LABEL_FAILURE),
    mergeMap((action) => {
      // TODO: figure out what error message actually gets sent
      const error = get(action, 'error', '');
      const voidNotificationProps = {
        title: <FormattedMessage {...messages.voidFailure} />,
        content: error.message,
        notificationType: NOTIFICATION_TYPE_ERROR,
      };
      NotificationService.showNotification({ ...voidNotificationProps });
      return EMPTY;
    }),
  );
};

export const returnsQuickSearch = (action$, { getState }) => {
  return action$.pipe(
    ofType(NavActions.RETURNS_SEARCH),
    mergeMap((action) => {
      const state = getState();
      const { searchData } = action;
      const returnStatus = Global.selectReturnsNavSelectedViewId(state);
      const page = Global.selectReturnsGridPage(state);
      const pageSize = Global.selectReturnsGridPageSize(state);
      const updatedSearchData = {
        ...searchData,
        returnStatus,
        page,
        pageSize,
      };

      return Api.getReturnsFromQuickSearch(updatedSearchData).pipe(
        map((data) => NavActions.returnsSearchSuccess(data)),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const returnsSearchTermCount = (action$, { getState }) => {
  return action$.pipe(
    ofType(NavActions.RETURNS_SEARCH_TERM_COUNT),
    mergeMap(() => {
      const state = getState();
      return Api.getReturnsSearchTermCount(Global.selectReturnsNavSearchData(state)).pipe(
        map((data) => NavActions.returnsSearchTermCountSuccess(data)),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const downloadReturnLabel = (action$) => {
  return action$.pipe(
    ofType(Actions.RETURNS_DOWNLOAD_RETURN_LABEL),
    mergeMap((action) => {
      const fulfillmentId = get(action, 'fulfillmentId');
      const url = `/api/download/label/${fulfillmentId}`;
      DocumentService.downloadFile(url);
      return of(ODActions.requestOrderDetails());
    }),
  );
};

export const printReturnLabelsFromGrid = (action$, { getState }) => {
  return action$.pipe(
    ofType(GridActions.RETURNS_GRID_PRINT_LABEL),
    mergeMap(() => {
      const state = getState();
      const selectedReturnIds = Global.selectSelectedReturnsSelectedReturnIds(state);
      const allTheReturns = Global.selectReturnsGridReturns(state);
      const returns = allTheReturns.filter((r) => selectedReturnIds.indexOf(r.fulfillmentId) > -1).map((r) => r.fulfillmentId);
      return of(PrintActions.printReturnLabels(returns));
    }),
  );
};

export const createReturnLabel = (action$) => {
  return action$.pipe(
    ofType(Actions.RETURNS_CREATE_LABEL),
    mergeMap(({ shippingConfiguration, emailTo, rmaNumber, metadata }) => {
      return Api.createReturnLabel(shippingConfiguration, emailTo, rmaNumber).pipe(
        mergeMap(({ fulfillmentId }) => {
          return of(Actions.createReturnLabelSuccess(shippingConfiguration, fulfillmentId, metadata));
        }),
        catchError((error) => {
          const errorMessages = get(error, 'response.data.Errors', []).map(({ Message }) => Message);
          createLabelSubject.next({
            fulfillmentPlanId: metadata.fulfillmentPlanId,
            errorMessages,
          });
          return of(Actions.createReturnLabelFailure(shippingConfiguration, errorMessages, metadata));
        }),
      );
    }),
  );
};

export const createReturnLabelSuccess = (action$) => {
  return action$.pipe(
    ofType(Actions.RETURNS_CREATE_LABEL_SUCCESS),
    mergeMap(({ fulfillmentId, metadata = {} }) => {
      if (metadata.notificationId) {
        NotificationService.removeNotification(metadata.notificationId);
      }

      if (metadata.actionCreator) {
        return of(
          metadata.actionCreator({
            ...metadata,
            fulfillmentId,
          }),
        );
      }

      return EMPTY;
    }),
  );
};

export const bulkloadAdvancedSearchEntitiesEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.RETURNS_ADVANCED_SEARCH_BULKLOAD_ENTITIES_REQUEST),
    mergeMap(({ returns }) => {
      // Extract fulfillment IDs from the returns data
      const fulfillmentIds = returns?.map((r) => r.fulfillmentId) ?? [];

      if (!fulfillmentIds || fulfillmentIds.length === 0) {
        return EMPTY;
      }
      return bulkLoadLabelFulfillments$(fulfillmentIds);
    }),
  );
};
